/**
 * Tutorials and help-support API.
 * Video create/update is multipart (`file` optional on update, `roleIds` as a JSON array).
 */

import { apiClient } from './api-client';
import type {
  HelpSupportContextEntry,
  HelpSupportEntry,
  HelpSupportMode,
  HelpSupportPlatform,
  TutorialCatalogMode,
  TutorialVideo,
  TutorialVideoFormState,
} from '@/types/tutorial.types';

/** `GET /tutorials?mode=` — videos visible to the caller in that mode. */
export const getTutorialVideos = async (mode: TutorialCatalogMode): Promise<TutorialVideo[]> => {
  const { data } = await apiClient.get<TutorialVideo[]>('/tutorials', { params: { mode } });
  return data;
};

/** `GET /tutorials/admin?mode=` — company videos for that screen, including an empty audience. */
export const getTutorialVideosAdmin = async (
  mode: TutorialCatalogMode,
): Promise<TutorialVideo[]> => {
  const { data } = await apiClient.get<TutorialVideo[]>('/tutorials/admin', { params: { mode } });
  return data;
};

/** `GET /tutorials/:id` */
export const getTutorialVideo = async (id: string): Promise<TutorialVideo> => {
  const { data } = await apiClient.get<TutorialVideo>(`/tutorials/${id}`);
  return data;
};

/** Build the multipart body shared by create and update. */
function toTutorialFormData(form: TutorialVideoFormState): FormData {
  const body = new FormData();
  body.append('name', form.name.trim());
  body.append('mode', form.mode);
  body.append('roleIds', JSON.stringify(form.roleIds));
  if (form.file) body.append('file', form.file);
  return body;
}

/** `POST /tutorials` */
export const createTutorialVideo = async (form: TutorialVideoFormState): Promise<TutorialVideo> => {
  const { data } = await apiClient.post<TutorialVideo>('/tutorials', toTutorialFormData(form));
  return data;
};

/** `PATCH /tutorials/:id` */
export const updateTutorialVideo = async (
  id: string,
  form: TutorialVideoFormState,
): Promise<TutorialVideo> => {
  const { data } = await apiClient.patch<TutorialVideo>(
    `/tutorials/${id}`,
    toTutorialFormData(form),
  );
  return data;
};

/** `DELETE /tutorials/:id` */
export const deleteTutorialVideo = async (id: string): Promise<void> => {
  await apiClient.delete(`/tutorials/${id}`);
};

/** `GET /help-support?platform=&mode=` */
export const getHelpSupport = async (
  platform: HelpSupportPlatform,
  mode: HelpSupportMode,
): Promise<HelpSupportEntry> => {
  const { data } = await apiClient.get<HelpSupportEntry>('/help-support', {
    params: { platform, mode },
  });
  return data;
};

/** `GET /help-support/admin` — the four phones and form URLs. */
export const getHelpSupportAdmin = async (): Promise<HelpSupportContextEntry[]> => {
  const { data } = await apiClient.get<HelpSupportContextEntry[]>('/help-support/admin');
  return data;
};

/** `PUT /help-support` — replace every phone and form URL. */
export const replaceHelpSupport = async (
  entries: HelpSupportContextEntry[],
): Promise<HelpSupportContextEntry[]> => {
  const { data } = await apiClient.put<HelpSupportContextEntry[]>('/help-support', {
    entries: entries.map((entry) => ({
      platform: entry.platform,
      mode: entry.mode,
      phone: entry.phone.trim(),
      url: entry.url.trim(),
    })),
  });
  return data;
};
